import isEmpty from "lodash/isEmpty";
import { FC, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router";

import { Pagination } from "@/components/Interface/Pagination/Pagination";
import { PageNumberComboBox } from "@/components/Layout/Table/PageNumberComboBox/PageNumberComboBox";
import {
  clampPage,
  clampPageSize,
} from "@/components/Layout/Table/PageNumberComboBox/pageSize";
import { Table } from "@/components/Layout/Table/Table";
import { additionalColumns } from "@/components/Portal/components/PortalDeviceListFilter/types";
import { useExtendedSims } from "@/hooks/useExtendedSims";
import { useGetSimQueryParamFilter } from "@/hooks/useGetSimQueryParamFilter";
import { useNeedsIDCheck } from "@/hooks/useNeedsIDCheck";
import { useGetSimList } from "@/services/api";

import { DeviceListHeaderRow } from "./DeviceListHeaderRow/DeviceListHeaderRow";
import { DeviceListRow } from "./DeviceListRow/DeviceListRow.tsx";

interface PortalDeviceListProps {
  manageRoute: string;
}

export const PortalDeviceList: FC<PortalDeviceListProps> = ({
  manageRoute,
}) => {
  const [searchParams] = useSearchParams();
  let filter = useGetSimQueryParamFilter();

  // There is (probably) a bug with orval where it generates the useGetSimListInfinite functions without properly
  // respecting the page param in the query key which leads to some weird caching issues wile using useGetSimList.
  // We solve this by always sending the page param here. The infinite queries cache key never has the page param.
  // see https://github.com/anymaniax/orval/issues/708
  const page = clampPage(searchParams.get("page"));
  filter = {
    ...filter,
    page: page - 1,
    pageSize: clampPageSize(searchParams.get("pageSize")),
  };

  const additionalColumnsString = searchParams.get("additionalColumns");
  const parsedColumns = useMemo(
    () =>
      additionalColumnsString
        ? Object.fromEntries(
            additionalColumnsString
              .split(",")
              .filter((column) =>
                (additionalColumns as ReadonlyArray<string>).includes(column),
              )
              .map((column) => [column, true]),
          )
        : undefined,
    [additionalColumnsString],
  );

  const [refetchInterval, setRefetchInterval] = useState<false | number>(false);
  const needsIdCheck = useNeedsIDCheck();

  const {
    data: simData,
    isPending: isLoadingSimData,
    isFetching: isFetchingSimData,
  } = useGetSimList(filter, {
    query: {
      refetchInterval,
      refetchOnMount: true,
      staleTime: 60 * 1000,
    },
  });

  // Enrich the list of Sims with roaming and esim data
  const {
    extendedSims,
    isLoading: isLoadingExtendedData,
    hasUnscannedEsims,
  } = useExtendedSims(simData?.simCards ?? [], refetchInterval, needsIdCheck);

  // Once we know there are unscanned esims, we set a refetch interval so all data gets renewed every x seconds
  useEffect(() => {
    setRefetchInterval(hasUnscannedEsims && 10000);
  }, [hasUnscannedEsims, setRefetchInterval]);

  const pages = simData?.totalNumberPages || 1;
  const noRows = isEmpty(simData?.simCards);

  const isFetching =
    isLoadingSimData || isFetchingSimData || isLoadingExtendedData;

  const showLoader = isFetching;
  const showContent =
    !isLoadingSimData && !isFetchingSimData && !isLoadingExtendedData;

  return (
    // Same card as the Bills table (see BillList): the ivory container
    // owns the surface, the shared Table runs `plain` inside it, and the
    // rows-per-page / pagination row is the card's footer.
    <div className="flex flex-col rounded-lg bg-surface-accent-ivory p-4">
      <Table
        variant="plain"
        hasPaddingBottom={false}
        noRows={noRows && !showLoader}
        isLoading={showLoader}
      >
        <thead>
          <DeviceListHeaderRow additionalColumns={parsedColumns} />
        </thead>
        <tbody>
          {showContent &&
            extendedSims.map((device) => {
              return (
                <DeviceListRow
                  key={device.simId}
                  additionalColumns={parsedColumns}
                  device={device}
                  manageRoute={manageRoute}
                />
              );
            })}
        </tbody>
      </Table>
      <div className="flex w-full flex-row items-center justify-between px-4 py-2">
        <PageNumberComboBox />
        {pages > 1 && <Pagination paginationPages={pages} />}
      </div>
    </div>
  );
};
